import { IMutator, IMutation, Mutation, IMutationContext } from './imutator';
import { ICommitChange, IResourceFilesProvider, ResourceProviderLocation } from 'app/interfaces/IResourceFilesProvider';
import { } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Shadow } from './shadow';
import { IResourceBase } from 'app/shared/utils/resourcefiles.utils';
import { SaveStatus, ISaveResult } from 'app/interfaces/ISaveStatus';
import {
    AdapterContext,
    A365Named,
    ChildResourceNamed,
    Notebook,
    A365BackendModel,
    DatabaseEntity,
    TreeNamedResAndNameUtils,
    IResourceContext
} from 'shell-models-lib';
import { Template } from 'app/model/template/template';
import { CrudEvent } from 'app/logger/logger.crud-event';
import { loggerConstants } from 'app/logger/logger.constants';
import { ILogger } from 'app/logger/ILogger';

export interface IGitMutationContext extends IMutationContext {
    comment: string;
}

export class ResourceMutator implements IMutator {
    protected static readonly className: string = 'ResourceMutator';

    constructor(
        private readonly _contextData: IResourceContext,
        private resourceFilesProvider?: IResourceFilesProvider,
        private logger?: ILogger
    ) { }

    public async mutate(mutations: IMutation[], context?: IMutationContext): Promise<ISaveResult> {
        const comment = context ? (context as IGitMutationContext).comment : undefined;
        const adapterContext = context ? context.adapterContext : AdapterContext.defaultContextOf(this._contextData);
        // We can do a combined mutation and then do the effects do achieve transactional
        // commits in git
        const changes: ICommitChange[] = [];
        for (const mutation of mutations) {
            const named = mutation.named;
            const model = Metamodel.modelOf(named);
            const concept = model.concept;

            if (named instanceof Template) { // template has two files to update
                changes.push(...named.getChangesFromMutation(mutation, adapterContext));
                continue;
            }
            /*
                We can save many types of resources for same named
            */
            const resourceMap = mutation.toResources ? await mutation.toResources(adapterContext, named) : undefined;
            if (!concept || !named.name) {
                throw new Error(`${model.name} cannot be persisted`);
            }
            if (resourceMap && (mutation.mutation === Mutation.ADD || mutation.mutation === Mutation.UPDATE)) {
                this._removeNotebookOutput(concept.name, resourceMap);
            }
            const resource: IResourceBase = {
                name: named.name,
                type: concept.name,
                subType: (model !== concept) ? model.name : undefined,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                folderName: (named as any).folder ? (named as any).folder.name : undefined
            };

            if (named instanceof A365Named) {
                resource.resourceType = named.resourceType;
            }

            if (named instanceof ChildResourceNamed && named.parentResource) {
                resource.parentResource = {
                    name: named.parentResource.name,
                    resourceType: Metamodel.modelOf(named.parentResource).name
                };
            }
            if (named instanceof DatabaseEntity && named.parentInfo) {
                // correct the name for sub-artifacts in database
                this._correctDbSubResourceInfo(resource, named);
            }

            let notGoIntoGitRepo = false;
            if (named instanceof A365Named && named.liveOnly) {
                notGoIntoGitRepo = true;
            }

            let resourcePayload;
            switch (mutation.mutation) {
                case Mutation.ADD:
                    if (!resourceMap) {
                        throw new Error('No resources to mutate');
                    }
                    resourcePayload = resourceMap[concept.name];
                    if (!context.adapterContext.writeIdentities) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (resourcePayload as any).name = resource.name;
                    }
                    changes.push(
                        {
                            operation: 'add',
                            resource,
                            payload: resourcePayload,
                            notGoIntoGitRepo
                        });
                    break;
                case Mutation.UPDATE:
                    if (!resourceMap) {
                        throw new Error('No resources to mutate');
                    }
                    resourcePayload = resourceMap[concept.name];
                    if (!context.adapterContext.writeIdentities) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (resourcePayload as any).name = resource.name;
                    }
                    changes.push(
                        {
                            operation: 'update',
                            resource,
                            payload: resourcePayload,
                            notGoIntoGitRepo
                        });
                    break;
                case Mutation.DELETE:
                    // In case of rename and delete, we use the original entity name as the renamed object does not have an associated file yet.
                    if (Shadow.shadow(mutation.named) && Shadow.shadow(mutation.named).name) {
                        const namedInShadow = Shadow.shadow(mutation.named);
                        resource.name = namedInShadow.name;
                        if (namedInShadow instanceof DatabaseEntity && namedInShadow.parentInfo) {
                            // correct the name for sub-artifacts in database
                            this._correctDbSubResourceInfo(resource, namedInShadow);
                        }
                    }
                    changes.push(
                        {
                            operation: 'delete',
                            resource,
                            notGoIntoGitRepo
                        });
                    break;
                case Mutation.RENAME:
                    const shadow = Shadow.shadow(named);
                    let oldName = shadow ? shadow.name : undefined;
                    if (shadow && shadow instanceof DatabaseEntity) {
                        oldName = shadow.realName;
                    }
                    changes.push(
                        {
                            operation: 'rename',
                            resource,
                            payload: { oldName },
                            notGoIntoGitRepo
                        });
                    break;
                case Mutation.NOOP:
                    break;
                default:
            }
        }

        if (this.resourceFilesProvider) {
            const result = await this.resourceFilesProvider.commit(changes, comment);
            if (this.logger && result) {
                changes.forEach((commitChange) => this.logger.logEvent(new CrudEvent(
                    loggerConstants.Operation_Commit,
                    commitChange.resource.name,
                    commitChange.resource.type,
                    commitChange.resource.type,
                    loggerConstants.Event_CRUD,
                    result && result.status ? SaveStatus[result.status] : undefined,
                    ResourceMutator.className,
                    commitChange.operation
                )));
            }
            if (result.status !== SaveStatus.Succeeded) {
                return result;
            }
        }

        for (const mutation of mutations) {
            if (mutation.then) {
                mutation.then();
            }
        }

        return { status: SaveStatus.Succeeded };
    }

    private _removeNotebookOutput(resourceType: string, resourceMap: { [key: string]: {} }): { [key: string]: {} } {
        if (__IS_A365__ &&
            resourceType === Notebook.MODEL.name &&
            this.resourceFilesProvider &&
            this.resourceFilesProvider.resourceProviderLocation === ResourceProviderLocation.Git) {
            const res = resourceMap[resourceType] as A365BackendModel.NotebookResource;
            if (res && res.properties && res.properties.cells) {
                for (const cell of res.properties.cells) {
                    const codeCell = cell as A365BackendModel.NotebookCodeCell;
                    if (codeCell) {
                        codeCell.outputs = undefined;
                    }
                }
            }
        }
        return resourceMap;
    }

    private _correctDbSubResourceInfo(resource: IResourceBase, named: DatabaseEntity): void {
        // correct the name for sub-artifacts in database
        resource.name = TreeNamedResAndNameUtils.extractNameAndParentName(resource.name).name;
        resource.parentResource = {
            name: named.parentInfo.resourceName,
            resourceType: named.parentInfo.resourceType
        };
    }
}
